/**
 * @description: This is Edit Master Credential Description Component that is responsible for editing the description of a master credential.
 */
import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { Subject } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { SiteManagerComponent } from 'src/app/site-management/site-manager/site-manager.component';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { FormControl, FormGroup } from '@angular/forms';
import { SiteManagementService } from 'src/app/site-management/site-management.service';
import { MFValidators } from 'src/app/shared/validators/mf-validators';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { CommonService } from 'src/app/shared/utils/common.service';
import { TranslateService } from '@ngx-translate/core';
import { IMasterCredential, ISite } from '../site-management-interfaces';
import { NavigationManagerService } from 'src/app/shared/services/navigation-manager.service';

@Component({
  selector: 'app-edit-master-credential-description',
  templateUrl: './edit-master-credential-description.component.html',
  styleUrls: ['./edit-master-credential-description.component.scss'],
})
export class EditMasterCredentialDescriptionComponent
  implements OnInit, OnDestroy
{
  public ngDestroyed$ = new Subject();
  @Input() public selectedSite: ISite | undefined;
  @Input() public credential: IMasterCredential | undefined;
  @Input() public enableBackOperation!: boolean | true;
  @Input() navigateToEditSite: any;

  editDescriptionForm: any;
  CONSTANTS = CONSTANTS;
  readonly LANG_CONST = LANG_CONST;
  sharedData!: any;
  previousDescription: string = '';

  constructor(
    public dialogRef: MatDialogRef<SiteManagerComponent>,
    private sharedDataService: SharedDataService,
    private siteManagementService: SiteManagementService,
    private notificationService: NotificationService,
    public commonService: CommonService,
    private translate: TranslateService,
    private navigationManagerService: NavigationManagerService
  ) {
    this.sharedDataService.currentData.subscribe((sharedData: any) => {
      this.sharedData = sharedData;
    });
  }

  /**
   * @description: runs on when component is initialized.
   * @input {none}
   * @output {void}
   */
  ngOnInit(): void {
    const credentialToUse =
      this.credential || this.sharedData?.selectedMasterCredential;
    const description = credentialToUse?.description ?? '';

    this.editDescriptionForm = new FormGroup({
      description: new FormControl(null, [
        MFValidators.required,
        MFValidators.maxLength(64),
        MFValidators.whitespace,
      ]),
    });

    this.editDescriptionForm.patchValue({
      description: credentialToUse.description?.trim(),
    });

    this.previousDescription = description;
  }

  /**
   * @description: Navigate back to Master credential detail screen
   * @input {none}
   * @output {void}
   */
  goBack(): void {
    this.navigationManagerService.setStepSiteManager(
      CONSTANTS.MASTER_CREDENTIAL_DETAIL
    );
  }

  /**
   * @description: Check if description has been modified
   * @returns {boolean}
   */
  isDescriptionChanged(): boolean {
    const currentDescription =
      this.editDescriptionForm?.controls?.description?.value;
    return this.previousDescription !== currentDescription;
  }

  /**
   * @description: Handle update description submission
   * @input {none}
   * @output {void}
   */
  onUpdateDescription(): void {
    if (!this.editDescriptionForm.valid) {
      return;
    }

    const credentialToUse =
      this.credential || this.sharedData?.selectedMasterCredential;
    if (!credentialToUse) {
      this.notificationService.showError(
        this.translate.instant('FAILED_TO_UPDATE_DESCRIPTION')
      );
      return;
    }
    const newDescription =
      this.editDescriptionForm?.controls?.description?.value;

    // If description hasn't changed, just navigate back
    if (!this.isDescriptionChanged()) {
      this.goBack();
      return;
    }

    const siteId =
      this.selectedSite?.siteId || this.sharedData?.selectedSite?.siteId;
    if (!siteId) {
      this.notificationService.showError(
        this.translate.instant('SITE_ID_NOT_FOUND')
      );
      return;
    }

    this.siteManagementService
      .updateMasterCard(credentialToUse.badgeId, siteId, newDescription)
      .subscribe({
        next: (res: any) => {
          if (res?.success) {
            this.notificationService.showSuccess(
              this.translate.instant('DESCRIPTION_UPDATED_SUCCESSFULLY')
            );
            this.sharedDataService.setSelectedMasterCredential({
              ...credentialToUse,
              description: newDescription,
            });
            this.goBack();
          } else {
            this.notificationService.showError(
              this.translate.instant('FAILED_TO_UPDATE_DESCRIPTION')
            );
          }
        },
        error: (err: Error) => {
          this.notificationService.showError(err?.message);
        },
      });
  }

  /**
   * @description: runs just before component unmount
   * @input {none}
   * @output {void}
   */
  ngOnDestroy(): void {
    this.ngDestroyed$.next(void 0);
    this.ngDestroyed$.complete();
  }
}
